import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

function IncidentalLoanPayment() {
  const navigate = useNavigate()
  const [loans, setLoans] = useState([])
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [formData, setFormData] = useState({
    noPinjaman: '',
    jumlahBayar: 0,
    sumberDana: 'kas',
    keterangan: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const loanSelectRef = useRef(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [namaKoperasi, setNamaKoperasi] = useState('Koperasi')
  const [calculation, setCalculation] = useState(null)
  const [allocation, setAllocation] = useState(null)
  const [settings, setSettings] = useState(null)

  useEffect(() => {
    fetchActiveLoans()
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await api.get('/incidental-loans/settings')
      if (response.data?.success) {
        // Settings is returned as array, get first active setting
        const settingsArray = response.data.data
        if (Array.isArray(settingsArray) && settingsArray.length > 0) {
          setSettings(settingsArray[0])
        } else {
          setSettings(settingsArray)
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  useEffect(() => {
    if (loanSelectRef.current && loans.length > 0) {
      $(loanSelectRef.current).select2({
        placeholder: '-- Pilih Pinjaman Insidentil --',
        allowClear: true,
        width: '100%'
      })

      $(loanSelectRef.current).on('change', (e) => {
        const noPinjaman = $(e.target).val()
        handleLoanChange(noPinjaman)
      })

      return () => {
        if (loanSelectRef.current) {
          $(loanSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [loans])

  const fetchActiveLoans = async () => {
    try {
      const response = await api.get('/incidental-loans?status=Dicairkan')
      setLoans(response.data.data || [])
    } catch (error) {
      console.error('Error fetching loans:', error)
    }
  }

  const handleLoanChange = async (noPinjaman) => {
    const selected = loans.find(l => l.NoPinjaman === noPinjaman)
    setSelectedLoan(selected)

    if (selected) {
      setFormData({
        ...formData,
        noPinjaman
      })
      calculatePaymentPreview(selected)
    } else {
      setSelectedLoan(null)
      setCalculation(null)
    }
  }

  const calculatePaymentPreview = async (loan) => {
    if (!settings) return

    try {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()

      // Get last bunga payment (JumlahBunga > 0 indicates actual interest payment, not pencairan)
      let lastBungaPayment = null
      try {
        const paymentResponse = await api.get(`/incidental-loans/${loan.NoPinjaman}/payments?limit=1`)
        const payments = paymentResponse.data.data || []
        lastBungaPayment = payments.find(p => parseFloat(p.JumlahBunga) > 0)
      } catch (e) {
        // No payments yet
      }

      // Check if bunga for current month has already been paid
      // Only true if there's an actual bunga payment record in current month
      let bungaSudahDibayar = false
      let lastPaymentDate

      if (lastBungaPayment) {
        const lastPaymentDateObj = new Date(lastBungaPayment.TglTransaksi)
        const lastPaymentYear = lastPaymentDateObj.getFullYear()
        const lastPaymentMonth = lastPaymentDateObj.getMonth()

        bungaSudahDibayar = (lastPaymentYear === currentYear && lastPaymentMonth === currentMonth)
        lastPaymentDate = lastPaymentDateObj
      } else {
        // No bunga payments yet, use pencairan date
        lastPaymentDate = new Date(loan.TglPencairan)
      }

      // Calculate days since last payment
      const daysSinceLastPayment = Math.floor((currentDate - lastPaymentDate) / (1000 * 60 * 60 * 24))

      // Calculate months difference
      const lastPaymentYear = lastPaymentDate.getFullYear()
      const lastPaymentMonth = lastPaymentDate.getMonth()
      let monthsCount = (currentYear - lastPaymentYear) * 12 + (currentMonth - lastPaymentMonth)
      monthsCount = Math.max(monthsCount + 1, 1)

      const sisaPokok = parseFloat(loan.SisaPokok)
      const bungaPerTahun = parseFloat(settings.PersentaseBunga)

      // Bunga per bulan = pokok * rate / 12
      const bungaPerBulan = sisaPokok * (bungaPerTahun / 100) / 12

      // Get prorata setting from settings
      const prorataAktif = settings.EnableProrata === 1 || settings.EnableProrata === true

      // Helper function to round to nearest 500
      const roundTo500 = (amount) => {
        return Math.round(amount / 500) * 500
      }

      // Calculate bunga
      let bungaBulanBerjalan = 0
      let daysProrata = 0
      if (!bungaSudahDibayar) {
        if (prorataAktif) {
          // Bulan pertama (pencairan): prorata dari tanggal pencairan ke akhir bulan pencairan
          const pencairanDate = new Date(loan.TglPencairan)
          const endOfMonth = new Date(pencairanDate.getFullYear(), pencairanDate.getMonth() + 1, 0)
          daysProrata = endOfMonth.getDate() - pencairanDate.getDate() + 1
          const firstMonthBunga = roundTo500((bungaPerBulan / 30) * daysProrata)

          // Bulan berikutnya: penuh
          const fullMonths = Math.max(monthsCount - 1, 0)
          const fullMonthsBunga = Math.round(bungaPerBulan * fullMonths)

          bungaBulanBerjalan = firstMonthBunga + fullMonthsBunga
        } else {
          bungaBulanBerjalan = Math.round(bungaPerBulan * monthsCount)
        }
      }

      // Denda: PersentaseDenda per bulan keterlambatan
      const dendaPerBulan = bungaBulanBerjalan * (parseFloat(settings.PersentaseDenda) / 100)
      const denda = monthsCount > 1 ? dendaPerBulan * (monthsCount - 1) : 0

      const calcData = {
        sisaPokok,
        bungaBulanBerjalan,
        bungaPerBulan,
        monthsCount: monthsCount || 1,
        denda,
        totalKewajiban: bungaBulanBerjalan + denda,
        lastPaymentMonth,
        lastPaymentYear,
        currentMonth,
        currentYear,
        bungaSudahDibayar,
        prorataAktif,
        daysSinceLastPayment,
        daysProrata,
        tglPencairan: loan.TglPencairan,
        isProrataApplied: prorataAktif && !bungaSudahDibayar
      }
      setCalculation(calcData)

      if (formData.jumlahBayar > 0) {
        calculateAllocation(formData.jumlahBayar, calcData)
      }
    } catch (error) {
      console.error('Error calculating preview:', error)
    }
  }

  const calculateAllocation = (jumlahBayar, calc) => {
    if (!calc) return

    const totalBungaBerjalan = calc.bungaBulanBerjalan || 0
    const totalDendaBerjalan = calc.denda || 0
    const kewajibanBungaDenda = totalBungaBerjalan + totalDendaBerjalan

    let sisaBayar = jumlahBayar

    // 1. Bayar bunga bulan berjalan dulu
    const alokasiBunga = Math.min(sisaBayar, totalBungaBerjalan)
    sisaBayar -= alokasiBunga

    // 2. Bayar denda berjalan
    const alokasiDenda = Math.min(sisaBayar, totalDendaBerjalan)
    sisaBayar -= alokasiDenda

    // 3. Sisanya masuk ke pokok
    const alokasiPokok = sisaBayar
    const sisaPokokBaru = calc.sisaPokok - alokasiPokok

    setAllocation({
      jumlahBayar,
      alokasiBunga,
      alokasiDenda,
      alokasiPokok,
      sisaPokokBaru: Math.max(0, sisaPokokBaru),
      kewajibanBungaDenda,
      isComplete: jumlahBayar >= kewajibanBungaDenda || kewajibanBungaDenda === 0
    })
  }

  const handleJumlahBayarChange = (value) => {
    const jumlah = parseFloat(value) || 0
    setFormData({ ...formData, jumlahBayar: jumlah })
    if (calculation) {
      calculateAllocation(jumlah, calculation)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await api.post(`/incidental-loans/${formData.noPinjaman}/pay`, {
        jumlahBayar: formData.jumlahBayar,
        sumberDana: formData.sumberDana
      })

      setNamaKoperasi(response.data.data.namaKoperasi || 'Koperasi')
      setPrintData({
        noPinjaman: formData.noPinjaman,
        namaAnggota: selectedLoan?.Nama,
        nba: selectedLoan?.NBA,
        jumlahBayar: formData.jumlahBayar,
        alokasiBunga: response.data.data.alokasiBunga,
        alokasiPokok: response.data.data.alokasiPokok,
        alokasiDenda: response.data.data.alokasiDenda,
        sisaPokokBaru: response.data.data.sisaPokok,
        tanggal: new Date().toLocaleDateString('id-ID')
      })
      setShowPrintModal(true)
      setFormData({
        ...formData,
        noPinjaman: '',
        jumlahBayar: 0,
        sumberDana: 'kas',
        keterangan: ''
      })
      setSelectedLoan(null)
      setCalculation(null)
      fetchActiveLoans()
    } catch (error) {
      setError(error.response?.data?.message || 'Gagal melakukan pembayaran')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <div className="card shadow border-0">
          <div className="card-header bg-primary text-white">
            <h5 className="m-0 font-weight-bold">Pembayaran Pinjaman Insidentil</h5>
          </div>
          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Pilih Pinjaman Insidentil *</label>
                <select
                  ref={loanSelectRef}
                  className="form-control select2"
                  style={{ width: '100%' }}
                  disabled={loading}
                >
                  <option value="">-- Pilih Pinjaman Insidentil --</option>
                  {loans.map((loan) => (
                    <option key={loan.NoPinjaman} value={loan.NoPinjaman}>
                      {loan.NoPinjaman} - {loan.Nama} - Dicairkan: {formatCurrency(loan.JumlahDicairkan || loan.SisaPokok)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedLoan && (
                <>
                  <div className="alert alert-info mb-3">
                    <strong>NBA:</strong> {selectedLoan.NBA} <br />
                    <strong>Nama:</strong> {selectedLoan.Nama} <br />
                    <strong>Jumlah Dicairkan:</strong> {formatCurrency(selectedLoan.JumlahDicairkan || selectedLoan.SisaPokok)} <br />
                    <strong>Sisa Pokok:</strong> {formatCurrency(selectedLoan.SisaPokok)} <br />
                    <strong>Bunga:</strong> {settings?.PersentaseBunga}% per tahun <br />
                    <strong>Denda:</strong> {settings?.PersentaseDenda}% per bulan <br />
                    <strong>Tgl Pencairan:</strong> {selectedLoan.TglPencairan ? new Date(selectedLoan.TglPencairan).toLocaleDateString('id-ID') : '-'}
                  </div>

                  {calculation && (
                    <div className="alert alert-secondary mb-3">
                      <h6 className="alert-heading">Perhitungan Pembayaran</h6>
                      <table className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Jumlah bulan yang belum dibayar</td>
                            <td className="text-right">{calculation.monthsCount} bulan</td>
                          </tr>
                          <tr>
                            <td>Bunga per bulan</td>
                            <td className="text-right">{formatCurrency(calculation.bungaPerBulan)}</td>
                          </tr>
                          <tr className="table-info">
                            <td>
                              {calculation.isProrataApplied
                                ? `Total bunga prorata (${calculation.daysProrata} hari)`
                                : `Total bunga (${calculation.monthsCount} bulan)`}
                            </td>
                            <td className="text-right">{formatCurrency(calculation.bungaBulanBerjalan)}</td>
                          </tr>
                          {calculation.isProrataApplied && (
                            <tr className="table-light">
                              <td><small className="text-muted">&nbsp;&nbsp;Bunga per bulan / 30 × {calculation.daysProrata} hari</small></td>
                              <td className="text-right"><small className="text-muted">{formatCurrency(calculation.bungaPerBulan)} / 30 × {calculation.daysProrata}</small></td>
                            </tr>
                          )}
                          <tr>
                            <td>Denda keterlambatan</td>
                            <td className="text-right">{formatCurrency(calculation.denda)}</td>
                          </tr>
                          <tr className="table-primary">
                            <td><strong>Total Kewajiban</strong></td>
                            <td className="text-right"><strong>{formatCurrency(calculation.totalKewajiban)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Jumlah Bayar *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.jumlahBayar}
                      onChange={(e) => handleJumlahBayarChange(e.target.value)}
                      required
                      min="0"
                      step="1000"
                    />
                  </div>

                  {allocation && (
                    <div className={`alert ${allocation.isComplete ? 'alert-success' : 'alert-warning'} mb-3`}>
                      <h6 className="alert-heading">Alokasi Pembayaran</h6>
                      <table className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Jumlah Bayar</td>
                            <td className="text-right">{formatCurrency(allocation.jumlahBayar)}</td>
                          </tr>
                          <tr className="table-warning">
                            <td>Alokasi Bunga ({calculation.monthsCount} bulan)</td>
                            <td className="text-right">- {formatCurrency(allocation.alokasiBunga)}</td>
                          </tr>
                          <tr className="table-warning">
                            <td>Alokasi Denda</td>
                            <td className="text-right">- {formatCurrency(allocation.alokasiDenda)}</td>
                          </tr>
                          <tr className="table-success">
                            <td>Alokasi Pokok Pinjaman</td>
                            <td className="text-right">- {formatCurrency(allocation.alokasiPokok)}</td>
                          </tr>
                          <tr className="table-primary">
                            <td><strong>Sisa Pokok Setelah Bayar</strong></td>
                            <td className="text-right"><strong>{formatCurrency(allocation.sisaPokokBaru)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                      {!allocation.isComplete && allocation.kewajibanBungaDenda > 0 && (
                        <div className="mt-2 text-danger">
                          <small><i className="fas fa-exclamation-circle"></i> Jumlah bayar kurang dari kewajiban bunga+denda ({formatCurrency(allocation.kewajibanBungaDenda)})</small>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Sumber Dana *</label>
                    <select
                      className="form-control"
                      value={formData.sumberDana}
                      onChange={(e) => setFormData({ ...formData, sumberDana: e.target.value })}
                      required
                    >
                      <option value="kas">Kas (Tunai)</option>
                      <option value="bank">Bank Transfer</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Keterangan</label>
                    <textarea
                      className="form-control"
                      value={formData.keterangan}
                      onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                      rows="2"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Memproses...' : 'Bayar'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {showPrintModal && printData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Bukti Pembayaran Pinjaman Insidentil</h5>
                <button type="button" className="close text-white" onClick={() => setShowPrintModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrintModal(false)}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const receiptContent = `
================================
  BUKTI PEMBAYARAN PINJAMAN INSIDENTIL
================================
No Pinjaman: ${printData.noPinjaman}
Tanggal    : ${printData.tanggal}
---------------------------------
DATA ANGGOTA
---------------------------------
NBA        : ${printData.nba}
Nama       : ${printData.namaAnggota}
---------------------------------
DETAIL PEMBAYARAN
---------------------------------
Jumlah Bayar    : Rp ${parseFloat(printData.jumlahBayar).toLocaleString('id-ID')}
---------------------------------
ALOKASI PEMBAYARAN
---------------------------------
Bunga           : Rp ${parseFloat(printData.alokasiBunga).toLocaleString('id-ID')}
Pokok           : Rp ${parseFloat(printData.alokasiPokok).toLocaleString('id-ID')}
Denda           : Rp ${parseFloat(printData.alokasiDenda).toLocaleString('id-ID')}
---------------------------------
SISA SETELAH BAYAR
---------------------------------
Sisa Pokok      : Rp ${parseFloat(printData.sisaPokokBaru).toLocaleString('id-ID')}
================================
  Tanda Tangan

(${namaKoperasi})
================================
`
                    const printWindow = window.open('', '_blank')
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Bukti Pembayaran Pinjaman Insidentil</title>
                          <style>
                            body {
                              font-family: 'Courier New', monospace;
                              font-size: 12px;
                              margin: 0;
                              padding: 10px;
                              white-space: pre;
                              line-height: 1.2;
                            }
                          </style>
                        </head>
                        <body>${receiptContent}</body>
                      </html>
                    `)
                    printWindow.document.close()
                    printWindow.print()
                  }}
                >
                  <i className="fas fa-print mr-1"></i> Cetak
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IncidentalLoanPayment
